/**
 * As ações que o serviço de automações pede ao CRM (#901): o corpo do
 * `POST /interno/acoes`.
 *
 * O contrato é do serviço (`api.cliqcrm.automacoes`, `docs/contrato.md`, com o
 * corpo fechado na #17 dele): uma união discriminada por `action`, com os
 * campos que toda ação leva. Os nomes ficam em inglês porque são os do
 * contrato entre os dois sistemas, e não do `/v1` da tela.
 *
 * Cada ação chama o MESMO caso de uso da tela (`apps/api/src/automacoes/acoes/`).
 * Os ids são os canônicos do CRM, e todo id é conferido contra a organização
 * do corpo assinado: o de outra organização responde 404.
 *
 * Acrescentar uma ação é acréscimo (não quebra): a variante aqui, o despacho
 * lá, e o serviço passa a mandá-la.
 */

import { z } from 'zod';
import { IdSchema } from './comum.js';

/** Os campos de toda ação (decisão da #17 do serviço, 18/10/2026). */
const Comum = {
  /** A chave da execução e do passo: reenviar a ação não repete o efeito. */
  idempotency_key: z
    .string()
    .regex(/^[A-Za-z0-9:_.-]{1,128}$/, 'Use letras, números e : _ . - (até 128).'),
  /** A organização. Tem de ser igual ao `x-tenant-id` assinado. */
  tenant_id: IdSchema,
  automation_id: z.string().regex(/^[A-Za-z0-9_-]{1,64}$/),
  automation_version: z.number().int().positive().max(999_999_999),
  execution_id: z.string().min(1).max(128),
  correlation_id: z.string().uuid(),
};

/** O motivo que vai para a trilha: os mesmos limites da tela (3 a 280). */
const Motivo = z.string().trim().min(3).max(280);

/** A referência de contato: `cpf:`, `cnpj:`, `tel:` ou o número. */
/** `cpf:`, `cnpj:`, `tel:` ou o número, ou `conversa:<id>` (o cliente da conversa). */
const ReferenciaDeContato = z.string().trim().min(1).max(60);

/**
 * A referência externa de uma oportunidade: o registro de outro sistema que a
 * fez nascer ou andar, como `<sistema>:<tipo>:<id do registro>`
 * (`sisac:exame:12345.3`). É o id do registro lá, e não um dado do paciente: o
 * formato barra nome e e-mail, mas não sabe distinguir um id de um CPF, e por
 * isso a tela pede a coluna do id.
 */
/** O mesmo do CHECK da tabela das referências (migração 20261027180000): mudar os dois juntos. */
const FORMATO_DA_REFERENCIA = '[a-z][a-z0-9_]{0,30}:[a-z][a-z0-9_]{0,30}:[A-Za-z0-9._/-]{1,100}';

const ReferenciaExterna = z
  .string()
  .trim()
  .regex(
    new RegExp(`^${FORMATO_DA_REFERENCIA}$`),
    'Use <sistema>:<tipo>:<id do registro>, como sisac:exame:12345.3.',
  );

/**
 * A oportunidade: o id dela; `conversa:<id>`, a da conversa do evento (a
 * vinculada a ela; sem vínculo, a que o cliente da conversa tem em aberto); ou
 * `ref:<referência>`, a que o "Criar oportunidade" ligou àquela referência.
 */
const ReferenciaDeOportunidade = z.union([
  IdSchema,
  z.string().regex(/^conversa:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i),
  z
    .string()
    .trim()
    .regex(/^ref:[A-Za-z0-9:_./-]{1,120}$/),
]);

export const AcaoDeAutomacaoSchema = z.discriminatedUnion('action', [
  z
    .object({
      action: z.literal('send_message'),
      ...Comum,
      conversation_id: IdSchema,
      /** O teto do WhatsApp é 4.096; o corpo inteiro cabe no limite do BFF. */
      content: z.string().max(10_000).optional(),
      template_name: z.string().min(1).max(200).optional(),
      template_values: z.array(z.string().max(1_000)).max(50).optional(),
    })
    .strict(),
  z
    .object({
      action: z.literal('send_menu'),
      ...Comum,
      conversation_id: IdSchema,
      /** O texto acima das opções. */
      text: z.string().trim().min(1).max(1_024),
      /** No WhatsApp: até 3 viram botões (rótulo até 20); de 4 a 10, lista (até 24). */
      options: z
        .array(
          z
            .object({ id: z.string().min(1).max(200), label: z.string().trim().min(1).max(24) })
            .strict(),
        )
        .min(1)
        .max(10),
    })
    .strict(),
  z
    .object({
      action: z.literal('move_stage'),
      ...Comum,
      opportunity_id: ReferenciaDeOportunidade,
      /** O `valor` canônico da etapa (`F_VENDAS_NOVO`), não o nome nem um UUID. */
      stage: z.string().min(1).max(200),
      /** Opcional: sem ela, o CRM lê a versão atual e move (#902). */
      version: z.string().min(1).optional(),
      /** O motivo de perda (id de `core.motivos_de_perda`), na etapa de perda. */
      loss_reason: IdSchema.optional(),
    })
    .strict(),
  z
    .object({
      action: z.literal('assign_conversation'),
      ...Comum,
      conversation_id: IdSchema,
      /** O usuário do CRM; `null` desatribui, e a caixa do motor redistribui. */
      user_id: IdSchema.nullable(),
      reason: Motivo,
    })
    .strict(),
  z
    .object({
      action: z.literal('assign_opportunity'),
      ...Comum,
      opportunity_id: ReferenciaDeOportunidade,
      user_id: IdSchema.nullable(),
      reason: Motivo,
    })
    .strict(),
  z
    .object({
      action: z.literal('create_task'),
      ...Comum,
      title: z.string().trim().min(1).max(500),
      due_at: z.string().datetime({ offset: true }).optional(),
      /** O responsável, pela referência externa do membro (a mesma da tela). */
      assignee_ref: z.string().min(1).max(200).optional(),
      /** Onde pendurar a tarefa. */
      target: z
        .object({ type: z.enum(['cliente', 'oportunidade', 'empresa']), id: IdSchema })
        .strict()
        .optional(),
      /** O CRM não a usa: a tarefa já sai com a automação de origem. Aceita, e pode faltar. */
      origin: z.string().max(200).optional(),
    })
    .strict(),
  z
    .object({
      action: z.literal('set_labels'),
      ...Comum,
      conversation_id: IdSchema,
      labels: z.array(z.string().trim().min(1).max(200)).max(20),
    })
    .strict(),
  z
    .object({
      action: z.literal('close_conversation'),
      ...Comum,
      conversation_id: IdSchema,
      status: z.enum(['resolvida', 'aberta']),
      reason: Motivo.optional(),
    })
    .strict(),
  z
    .object({
      action: z.literal('notify_manager'),
      ...Comum,
      conversation_id: IdSchema.optional(),
      /** A chave do aviso (`no_first_reply`). O texto é da tela do CRM. */
      kind: z.string().regex(/^[a-z0-9_]{1,64}$/),
      /** Texto livre do motor: aceito, e NÃO guardado (#903). */
      detail: z.string().max(2_000).optional(),
    })
    .strict(),
  z
    .object({
      action: z.literal('create_opportunity'),
      ...Comum,
      stage: z.string().min(1).max(200),
      /**
       * O contato: `cpf:<número>`, `cnpj:<número>`, o telefone (`tel:<número>`
       * ou só o número), ou `conversa:<id>`, o cliente vinculado à conversa. Sem
       * ele, a oportunidade nasce sem contato.
       */
      contact_ref: ReferenciaDeContato.optional(),
      /**
       * Só os campos que o CRM usa; os outros são descartados na leitura, para
       * o motor não ter por onde mandar dado pessoal que ninguém usa.
       */
      fields: z
        .object({
          nome: z.string().trim().min(1).max(500).optional(),
          nome_do_contato: z.string().trim().min(1).max(200).optional(),
        })
        .optional(),
      /** Por onde a oportunidade nasceu. O CRM não a usa: aceita, e pode faltar. */
      origin: z.string().max(200).optional(),
      /**
       * O registro de fora que pede o card (`sisac:exame:<movimento>`). Já
       * visto: devolve a mesma oportunidade e não faz nada. Novo: segue a regra
       * de sempre e liga a referência ao card, para o `ref:` do `move_stage`.
       * Exige o `contact_ref`: é pelo contato do card que o titular a alcança.
       */
      external_ref: ReferenciaExterna.optional(),
      /**
       * O contato já tem um card em aberto no funil: `keep` o deixa onde está
       * (o padrão); `move` o leva à etapa pedida.
       */
      when_open: z.enum(['keep', 'move']).optional(),
      /**
       * O valor do registro, em reais (#1143): o card novo nasce com ele; no
       * card do contato que já existia, a referência NOVA soma o valor dela ao
       * do card. Sem `external_ref`, só vale para o card novo: sem a referência
       * não há como saber se a linha já foi somada. A coluna do SQL pode chegar
       * como número ou como texto numérico (`120.50`, `120,50`).
       */
      value: z
        .union([
          z.number(),
          // Texto numérico como o banco o entrega: espaços em volta, ponto ou vírgula decimal.
          z
            .string()
            .trim()
            .regex(/^\d{1,12}(?:[.,]\d{1,12})?$/)
            .transform((v) => Number(v.replace(',', '.'))),
        ])
        // Até R$ 10 milhões por registro: a soma em micros fica longe do inteiro seguro.
        .pipe(z.number().nonnegative().max(10_000_000))
        .nullable()
        .optional(),
    })
    .strict(),
  z
    .object({
      action: z.literal('create_contact'),
      ...Comum,
      name: z.string().trim().min(1).max(200).optional(),
      /** Só os campos que o CRM usa (ver `create_opportunity`). */
      fields: z.object({ email: z.string().trim().min(1).max(200).optional() }).optional(),
      /** Como achar o contato que já existe: a mesma forma do `contact_ref`. */
      dedupe_ref: ReferenciaDeContato.optional(),
    })
    .strict(),
]);

export type AcaoDeAutomacao = z.infer<typeof AcaoDeAutomacaoSchema>;

export type NomeDaAcaoDeAutomacao = AcaoDeAutomacao['action'];

/**
 * A resposta de uma ação feita: o que o motor guarda no passo.
 *
 * Só ids, estados e versões — nunca nome, título ou texto. O motor guarda a
 * resposta no histórico dele, fora do inventário e da retenção do CRM; e ela
 * também fica na idempotência por um dia.
 */
export const ResultadoDaAcaoSchema = z
  .object({
    action: z.string(),
    resultado: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])),
  })
  .strict();

export type ResultadoDaAcao = z.infer<typeof ResultadoDaAcaoSchema>;
